"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useReturnFocus = useReturnFocus;
exports.useElement = useElement;
exports.useKeepFocusWithin = useKeepFocusWithin;

var _compact = _interopRequireDefault(require("lodash/compact"));

var _get = _interopRequireDefault(require("lodash/get"));

var _isEmpty = _interopRequireDefault(require("lodash/isEmpty"));

var _react = require("react");

var _canUseDOM = _interopRequireDefault(require("../../utils/canUseDOM"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance"); }

function _iterableToArrayLimit(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

/**
 * A React hook that returns the focus to the previous location when the arg is
 * toggled from a falsy value to a truthy value. This hook must be called with a
 * falsy value first.
 *
 * @param {boolean} returnFocus
 */
function useReturnFocus(returnFocus) {
  var _useState = (0, _react.useState)(null),
      _useState2 = _slicedToArray(_useState, 2),
      previousFocus = _useState2[0],
      setPreviousFocus = _useState2[1];

  (0, _react.useEffect)(function () {
    if (!returnFocus && !previousFocus) {
      setPreviousFocus(document.activeElement);
    }

    if (returnFocus && previousFocus) {
      previousFocus.focus();
    }
  }, [returnFocus, previousFocus]);
}
/**
 * A React hook that returns the HTMLElement for a ref.
 *
 * @returns {([
 *   HTMLElement | null,
 *   (node: HTMLElement | null) => void,
 * ])}
 */


function useElement() {
  var _useState3 = (0, _react.useState)(null),
      _useState4 = _slicedToArray(_useState3, 2),
      element = _useState4[0],
      setElement = _useState4[1];

  var ref = (0, _react.useCallback)(function (node) {
    if (node !== null) {
      setElement(node);
    }
  }, []);
  return [element, ref];
}
/**
 * A query that returns all elements that the user should be able to navigate to using "Tab"
 *
 * @param {(HTMLElement | null)} container
 * @returns {Element[]}
 */


function useTabbableElements(container) {
  // The core query here is borrowed from https://kennethbi.com/posts/5
  return container && (0, _compact.default)(Array.from(container.querySelectorAll("\n            input:not([tabindex^=\"-\"]):not([disabled]),\n            select:not([tabindex^=\"-\"]):not([disabled]),\n            textarea:not([tabindex^=\"-\"]):not([disabled]),\n            button:not([tabindex^=\"-\"]):not([disabled]),\n            a[href]:not([tabindex^=\"-\"]):not([disabled]),\n            [tabindex]:not([tabindex^=\"-\"]):not([disabled])\n          ")).filter(function (element) {
    return element.offsetParent !== null;
  })) || [];
}
/**
 * A React hook that sets and maintains a user's focus within a specific tree in the DOM. On
 * initial render the first "tabbable" element will be focused.
 *
 * @param {(HTMLElement | null)} container
 */


function useKeepFocusWithin(container) {
  var tabbableElements = useTabbableElements(container); // This effect should run only when the container changes from null to an element. Since
  // the component does not control the tabbable elements and they may be updated, we
  // don't want to automatically change the focus at any other time.

  (0, _react.useEffect)(function () {
    if (container && !(0, _isEmpty.default)(tabbableElements)) {
      // The first tabbable element will be the close button, but that isn't where
      // the user's focus should start. Instead, get the second tabbable element.
      var secondTabbable = (0, _get.default)(tabbableElements, 1, {
        focus: function focus() {}
      });
      secondTabbable.focus();
    }
  }, [container]); // eslint-disable-line react-hooks/exhaustive-deps

  var onKeyDown = (0, _react.useCallback)(function (event) {
    // "Trap" all presses of the "Tab" key
    var isTabKey = event.code === 'Tab' || event.keyCode === 9;

    if (isTabKey) {
      if ((0, _isEmpty.default)(tabbableElements) || tabbableElements.length < 2) {
        // There are no tabbable elements within the container. Kill the event.
        event.preventDefault();
      } else if (event.shiftKey) {
        if (document.activeElement === tabbableElements[0]) {
          // The user is going backward through the tabbable elements and needs to loop.
          event.preventDefault();
          tabbableElements[tabbableElements.length - 1].focus();
        }
      } else if (document.activeElement === tabbableElements[tabbableElements.length - 1]) {
        // The user is going forward through the tabbable elements and needs to loop.
        event.preventDefault();
        tabbableElements[0].focus();
      }
    }
  }, [tabbableElements]); // Allow closing the modal by pressing the Escape key

  (0, _react.useEffect)(function () {
    if (_canUseDOM.default) {
      document.addEventListener('keydown', onKeyDown);
      return function () {
        document.removeEventListener('keydown', onKeyDown);
      };
    } // eslint has issues with no return statement here...


    return; // eslint-disable-line consistent-return, no-useless-return
  }, [container, onKeyDown]);
}